import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

// ============================================================
// VideoCallRoom
// Ye component 2 kaam karta hai:
// 1. Agar patient hai aur therapist abhi tak join nahi hua,
//    to "waiting" screen dikhata hai (har 5 sec check karta hai)
// 2. Jaise hi therapist join kare (ya khud therapist ho), Jitsi
//    ka actual video call load kar deta hai
// ============================================================

const VideoCallRoom = ({
  appointmentId,
  userType,          // "patient" ya "therapist"
  baseUrl,
  authHeaders,
  onClose,
}) => {
  const [status, setStatus] = useState("loading"); // loading | waiting | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [roomData, setRoomData] = useState(null);
  const pollTimerRef = useRef(null);

  // ── Backend se room details lena (join endpoint) ────────────
  const fetchJoinDetails = useCallback(() => {
    const endpoint =
      userType === "patient"
        ? `${baseUrl}/patient/Booking/${appointmentId}/join`
        : `${baseUrl}/doctor/Bookings/${appointmentId}/join`;

    return axios.get(endpoint, { headers: authHeaders });
  }, [appointmentId, userType, baseUrl, authHeaders]);

  // ── Pehli dafa load hote hi call karo ───────────────────────
  useEffect(() => {
    let isMounted = true;

    fetchJoinDetails()
      .then((res) => {
        if (!isMounted) return;

        const data = res.data.data;
        setRoomData(data);

        // Patient hai aur therapist abhi tak nahi aaya → waiting screen
        if (userType === "patient" && !data.therapist_joined) {
          setStatus("waiting");
        } else {
          setStatus("ready");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setErrorMsg(
          err?.response?.data?.message ||
            "Session link is not available right now."
        );
        setStatus("error");
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Agar "waiting" state mein hain, har 5 sec check karte raho ──
  useEffect(() => {
    if (status !== "waiting") return;

    pollTimerRef.current = setInterval(() => {
      fetchJoinDetails()
        .then((res) => {
          const data = res.data.data;
          if (data.therapist_joined) {
            setRoomData(data);
            setStatus("ready");
          }
        })
        .catch(() => {
          // Polling error ko chup chap ignore karte hain,
          // agla interval phir try karega
        });
    }, 5000);

    return () => clearInterval(pollTimerRef.current);
  }, [status, fetchJoinDetails]);


  // ============================================================
  // UI
  // ============================================================

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>

        {/* Close button hamesha upar right corner pe */}
        <button style={styles.closeBtn} onClick={onClose}>
          ✕
        </button>

        {status === "loading" && (
          <div style={styles.centerBox}>
            <div style={styles.spinner}></div>
            <p style={styles.text}>Session load ho rahi hai...</p>
          </div>
        )}

        {status === "waiting" && (
          <div style={styles.centerBox}>
            <div style={styles.spinner}></div>
            <h3 style={styles.title}>Therapist ka intezaar hai</h3>
            <p style={styles.text}>
              Aapki appointment confirmed hai. Jaise hi therapist session join
              karenge, call automatically shuru ho jayegi.
            </p>
          </div>
        )}

        {status === "error" && (
          <div style={styles.centerBox}>
            <h3 style={{ ...styles.title, color: "#dc2626" }}>
              Session Available Nahi
            </h3>
            <p style={styles.text}>{errorMsg}</p>
            <button style={styles.retryBtn} onClick={onClose}>
              Band Karein
            </button>
          </div>
        )}

{status === "ready" && roomData && (
  <iframe
    src={roomData.room_url}
    allow="camera; microphone; fullscreen; display-capture; autoplay"
    style={styles.jitsiBox}
    title="Video Session"
  />
)}
      </div>
    </div>
  );
};

// ============================================================
// Inline styles (aap chahen to alag CSS file mein move kar sakti hain)
// ============================================================

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.85)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    position: "relative",
    width: "95%",
    height: "90%",
    background: "#111",
    borderRadius: "12px",
    overflow: "hidden",
  },
  closeBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: 10,
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    fontSize: "18px",
    cursor: "pointer",
  },
  centerBox: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    textAlign: "center",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid rgba(255,255,255,0.2)",
    borderTopColor: "#4CAF50",
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
    marginBottom: "1.5rem",
  },
  title: {
    color: "#fff",
    fontSize: "1.3rem",
    marginBottom: "0.5rem",
  },
  text: {
    color: "#cbd5e1",
    fontSize: "0.95rem",
    maxWidth: "400px",
  },
  retryBtn: {
    marginTop: "1.5rem",
    padding: "10px 24px",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  jitsiBox: {
    width: "100%",
    height: "100%",
  },
};

export default VideoCallRoom;